/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { EcsperAuthService } from './auth-service.js';
import type { AuthState } from './auth-state.js';

interface EcsperAuthContextValue {
  readonly state: AuthState;
  readonly authenticate: () => Promise<void>;
  readonly selectOffice: (officeId: string) => Promise<void>;
  readonly refresh: () => Promise<void>;
  readonly logout: () => void;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
}

const EcsperAuthContext = createContext<EcsperAuthContextValue | null>(null);

/**
 * React context provider for Ecsper authentication.
 *
 * Wraps {@link EcsperAuthService} and exposes auth state and actions via {@link useEcsperAuth}.
 * Place at the root of the task pane component tree.
 */
export const EcsperAuthProvider: FC<{ children: ReactNode; service: EcsperAuthService }> = ({
  children,
  service,
}) => {
  const [state, setState] = useState<AuthState>(() => service.getState());

  useEffect(() => {
    setState(service.getState());
    return service.subscribe(setState);
  }, [service]);

  const authenticate = useCallback(async () => {
    await service.authenticate();
  }, [service]);

  const selectOffice = useCallback(
    async (officeId: string) => {
      await service.authenticateWithOffice(officeId);
    },
    [service],
  );

  const refresh = useCallback(async () => {
    await service.refresh();
  }, [service]);

  const logout = useCallback(() => {
    service.logout();
  }, [service]);

  const value = useMemo<EcsperAuthContextValue>(() => {
    const loading =
      state.status === 'bootstrapping' ||
      state.status === 'exchanging' ||
      state.status === 'refreshing';
    return {
      state,
      authenticate,
      selectOffice,
      refresh,
      logout,
      isAuthenticated: state.status === 'authenticated',
      isLoading: loading,
    };
  }, [authenticate, logout, refresh, selectOffice, state]);

  return <EcsperAuthContext.Provider value={value}>{children}</EcsperAuthContext.Provider>;
};

export function useEcsperAuth(): EcsperAuthContextValue {
  const ctx = useContext(EcsperAuthContext);
  if (ctx === null) {
    throw new Error('useEcsperAuth must be used within an EcsperAuthProvider');
  }
  return ctx;
}
